import React, {useEffect, useState} from 'react';
import {
  calculate,
  calculateSchlichBySex,
  formatArea,
  formatPercent,
  FORMULA_LABELS,
  toMetric,
  validate
} from '../lib/calculations';

export default function Calculator({onResult}) {
  const [unit, setUnit] = useState('metric');
  const [height, setHeight] = useState('170');
  const [weight, setWeight] = useState('70');
  const [formula, setFormula] = useState('Mosteller');
  const [compareAll, setCompareAll] = useState(false);
  const [sex, setSex] = useState('male');
  const error = validate(height, weight, unit);
  const metric = toMetric(height, weight, unit);
  const results = error ? null : calculate(metric.height, metric.weight, sex);
  const allSchlich = error ? null : calculateSchlichBySex(metric.height, metric.weight);
  const selectedValue = results?.[formula];

  useEffect(() => {
    onResult(selectedValue ?? null, error ? null : metric, formula, results);
  }, [height, weight, unit, formula, sex, selectedValue, error, onResult]);

  const switchUnit = next => {
    if (next === unit) return;
    const converted = toMetric(height, weight, unit);
    setHeight(height === '' ? '' : (next === 'metric' ? converted.height : converted.height / 2.54).toFixed(1));
    setWeight(weight === '' ? '' : (next === 'metric' ? converted.weight : converted.weight / 0.45359237).toFixed(1));
    setUnit(next);
  };

  const maxDifference = results
    ? Math.max(...FORMULA_LABELS.flatMap(label => {
      if (label === 'Schlich') {
        return Object.values(allSchlich).map(value => Math.abs(value - results.Mosteller));
      }
      return Math.abs(results[label] - results.Mosteller);
    })) / results.Mosteller * 100
    : 0;

  return (
    <section className="panel calculator" id="calculadora" aria-labelledby="calc-title">
      <div className="section-heading">
        <h2 id="calc-title">Quais são as suas medidas?</h2>
        <p>Use centímetros e quilogramas ou alterne para polegadas e libras.</p>
      </div>

      <div className="unit-switch" role="group" aria-label="Unidades de medida">
        <button type="button" className={unit === 'metric' ? 'active' : ''} aria-pressed={unit === 'metric'} onClick={() => switchUnit('metric')}>cm / kg</button>
        <button type="button" className={unit === 'imperial' ? 'active' : ''} aria-pressed={unit === 'imperial'} onClick={() => switchUnit('imperial')}>pol / lb</button>
      </div>

      <div className="field-grid">
        <label>Altura <span>({unit === 'metric' ? 'cm' : 'pol'})</span><input type="number" inputMode="decimal" min="0" step="any" value={height} onChange={event => setHeight(event.target.value)} aria-invalid={!!error && (height === '' || metric.height < 30 || metric.height > 250)} /></label>
        <label>Peso <span>({unit === 'metric' ? 'kg' : 'lb'})</span><input type="number" inputMode="decimal" min="0" step="any" value={weight} onChange={event => setWeight(event.target.value)} aria-invalid={!!error && (weight === '' || metric.weight < 1 || metric.weight > 400)} /></label>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      <div className="formula-select">
        <label htmlFor="formula-principal">Fórmula principal</label>
        <select id="formula-principal" value={formula} onChange={event => setFormula(event.target.value)}>
          {FORMULA_LABELS.map(label => <option key={label} value={label}>{label}</option>)}
        </select>
        {formula === 'Schlich' && (
          <label className="sex-select" htmlFor="schlich-sex">Variante da fórmula
            <select id="schlich-sex" value={sex} onChange={event => setSex(event.target.value)}>
              <option value="male">Homens</option>
              <option value="female">Mulheres</option>
            </select>
          </label>
        )}
        <label className="compare-toggle"><input type="checkbox" checked={compareAll} onChange={event => setCompareAll(event.target.checked)} /> Comparar todas as fórmulas</label>
      </div>

      {results && (
        <div className="result" aria-live="polite">
          <span>Resultado principal · {formula}</span>
          <strong>{formatArea(selectedValue)} <small>m²</small></strong>
          <p>{formula} · altura {metric.height.toFixed(1).replace('.', ',')} cm · peso {metric.weight.toFixed(1).replace('.', ',')} kg</p>
        </div>
      )}

      {compareAll && results && (
        <div className="formula-comparison" aria-live="polite">
          <h3>Como as fórmulas se comparam?</h3>
          <p>Diferença percentual em relação a Mosteller. Schlich mostra as duas variantes, sem presumir essa informação.</p>
          <div className="table-scroll">
            <table>
              <thead><tr><th scope="col">Fórmula</th><th scope="col">Resultado (m²)</th><th scope="col">Diferença vs. Mosteller</th></tr></thead>
              <tbody>
                {FORMULA_LABELS.map(label => {
                  if (label === 'Schlich') {
                    return (
                      <React.Fragment key={label}>
                        {Object.entries(allSchlich).map(([variant, value]) => (
                          <tr key={variant}><th scope="row">Schlich · {variant}</th><td>{formatArea(value)}</td><td>{formatPercent((value - results.Mosteller) / results.Mosteller * 100)}%</td></tr>
                        ))}
                      </React.Fragment>
                    );
                  }
                  return <tr key={label}><th scope="row">{label}</th><td>{formatArea(results[label])}</td><td>{formatPercent((results[label] - results.Mosteller) / results.Mosteller * 100)}%</td></tr>;
                })}
              </tbody>
            </table>
          </div>
          {maxDifference > 5 && <p className="formula-warning" role="status"><strong>Atenção:</strong> pelo menos uma estimativa difere mais de 5% de Mosteller. Isso não indica qual resultado está correto; confira o protocolo aplicável.</p>}
        </div>
      )}

      <p className="formula-note">Altura em centímetros, peso em quilogramas e superfície corporal em metros quadrados. As dez equações e seus contextos estão descritos na biblioteca.</p>
    </section>
  );
}
